import type {
  TextValue,
  VariableMeta,
  VariableType,
  Zone,
  ZoneComponent,
} from '@tsagko/label-contracts'
import { GS1_AI_DICTIONARY } from '@tsagko/label-contracts'

const SINGLE_VARIABLE_RE = /^\s*\{([a-zA-Z][a-zA-Z0-9_]*)\}\s*$/

/**
 * Returns the inner variable name if `content` matches the single-variable
 * form (`{name}` with optional surrounding whitespace), or undefined otherwise.
 *
 * Tolerant by design: pure literals, mid-typing input (`{`, `{ab`, `pre {x`),
 * and mixed content all return undefined. The schema-level constraint
 * (`TextValueSchema` with its pure-literal-or-single-variable refinement)
 * rejects malformed content on save, so render-time tolerance is safe and
 * prevents the route from crashing while the user is still typing.
 */
export function extractVariableName(content: string): string | undefined {
  const single = SINGLE_VARIABLE_RE.exec(content)
  return single ? single[1] : undefined
}

const BRACE_PAIR_RE = /\{[^{}]*\}/

/**
 * True when `content` contains a `{...}` pair but does NOT resolve as a clean
 * single-variable value per `extractVariableName` — i.e. the braces will print
 * literally and no variable is derived. Drives the informational
 * mixed-variable note in the properties panel.
 *
 * Single-sourced with SINGLE_VARIABLE_RE so the note can never disagree with
 * what actually derives a variable: `" {lot} "` (whitespace-padded) resolves
 * cleanly → no note; `"{_x}"` is not a valid variable name → note.
 */
export function isMixedBraceText(content: string): boolean {
  return BRACE_PAIR_RE.test(content) && extractVariableName(content) === undefined
}

function extractFromTextValue(value: TextValue | undefined): string | undefined {
  if (!value) return undefined
  return extractVariableName(value.content)
}

function collectFromComponent(c: ZoneComponent | undefined, out: string[]) {
  if (!c) return
  if (c.type === 'baked-block-instance') return
  if (c.type === 'text') {
    const v = extractFromTextValue(c.value)
    if (v && !out.includes(v)) out.push(v)
  } else if (c.type === 'symbol') {
    const ds = c.dataStructure
    if (ds.kind === 'raw' || ds.kind === 'gtin' || ds.kind === 'url') {
      const v = extractFromTextValue(ds.value)
      if (v && !out.includes(v)) out.push(v)
    } else if (ds.kind === 'gs1-ai') {
      for (const el of ds.elements) {
        const v = extractFromTextValue(el.value)
        if (v && !out.includes(v)) out.push(v)
      }
    }
  } else if (c.type === 'localized-text' || c.type === 'localized-rich-text') {
    for (const variant of c.variants) {
      const v = extractFromTextValue(variant.value)
      if (v && !out.includes(v)) out.push(v)
    }
  }
}

function walk(zone: Zone, out: string[]) {
  if (zone.component) collectFromComponent(zone.component, out)
  if (zone.children) {
    walk(zone.children[0], out)
    walk(zone.children[1], out)
  }
}

/** Returns unique variable names referenced anywhere in `layout`, in first-encountered order. */
export function collectAllVariableNames(layout: Zone): string[] {
  const out: string[] = []
  walk(layout, out)
  return out
}

/**
 * Walks a layout and collects, for each variable name referenced by a gs1-ai
 * element, the AI's `defaultVarType`. First occurrence wins; does not overwrite.
 */
function collectGs1AiTypeHints(zone: Zone, out: Record<string, VariableType>) {
  const c = zone.component
  if (c && c.type === 'symbol' && c.dataStructure.kind === 'gs1-ai') {
    for (const el of c.dataStructure.elements) {
      const v = extractFromTextValue(el.value)
      if (v && !(v in out)) {
        out[v] = GS1_AI_DICTIONARY[el.ai]?.defaultVarType ?? 'text'
      }
    }
  }
  if (zone.children) {
    collectGs1AiTypeHints(zone.children[0], out)
    collectGs1AiTypeHints(zone.children[1], out)
  }
}

const TEXT_DEFAULT: VariableMeta = { type: 'text', default: '' }

/** Merges persisted metadata with the layout-derived variable set. */
export function mergeVariables(
  layout: Zone,
  persisted: Record<string, VariableMeta> = {}
): Record<string, VariableMeta> {
  const names = collectAllVariableNames(layout)
  const hints: Record<string, VariableType> = {}
  collectGs1AiTypeHints(layout, hints)
  const out: Record<string, VariableMeta> = {}
  for (const name of names) {
    out[name] = persisted[name] ?? { type: hints[name] ?? 'text', default: '' }
  }
  return out
}

/** Drops persisted entries for variables no longer referenced anywhere. */
export function pruneOrphans(
  layout: Zone,
  persisted: Record<string, VariableMeta> = {}
): Record<string, VariableMeta> {
  const referenced = new Set(collectAllVariableNames(layout))
  const out: Record<string, VariableMeta> = {}
  for (const [name, meta] of Object.entries(persisted)) {
    if (referenced.has(name)) out[name] = meta
  }
  return out
}
